import './ExportReports.scss';

import * as React from 'react';

import { ActivityType, ReportTypes } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';

import { Dialog, DialogProps, DialogTitle } from '~shared/components/Dialog';
import { TabList, TabPanel, TabPanels } from '~shared/components/Tabs';
import {
  ModalMenu,
  ModalMenuContent,
  ModalMenuProvider,
  ModalMenuSidebar,
  ModalMenuTab,
} from '~teacher/components/ModalMenu';
import { usePaywallContext } from '~teacher/components/PaywallDialog';
import { getReportSettings } from '~teacher/helpers/ReportSettings';
import { proCheck } from '~teacher/util/proCheck';

import * as reportsApi from '../../../api/reports/reportsApi';
import ExportReportsAnswerKey from './ExportReportsAnswerKey';
import { ExportReportsCompleteResults } from './ExportReportsCompleteResults';
import { ExportReportsIndividual } from './ExportReportsIndividual';
import { ExportReportsPaywall } from './ExportReportsPaywall';
import { ExportReportsSummary } from './ExportReportsSummary';

interface Props extends Pick<DialogProps, 'open' | 'onClose'> {
  activityInstanceId: number;
  activityType: ActivityType;
  isSaveToDriveDisabled: boolean;
  onDownloadReports?: typeof reportsApi.downloadReports;
  onUploadToDrive?: typeof reportsApi.uploadToDrive;
  onEmailReports?: typeof reportsApi.emailReportsToTeacher;
}

export interface UIProps extends Props {
  canCustomizeExportResults: boolean;
}

export function ExportReports({
  open,
  onClose,
  isSaveToDriveDisabled,
  activityInstanceId,
  activityType,
  onDownloadReports = reportsApi.downloadReports,
  onUploadToDrive = reportsApi.uploadToDrive,
  onEmailReports = reportsApi.emailReportsToTeacher,
}: React.PropsWithChildren<Props>) {
  const canCustomizeExportResults = proCheck.canCustomizeExportResults();
  const uiProps = {
    open,
    isSaveToDriveDisabled,
    activityInstanceId,
    activityType,
    onClose,
    onDownloadReports,
    onUploadToDrive,
    onEmailReports,
    canCustomizeExportResults,
  };

  return <ExportReportsUI {...uiProps} />;
}

export function ExportReportsUI({
  open,
  onClose,
  isSaveToDriveDisabled,
  activityInstanceId,
  activityType,
  onDownloadReports,
  onUploadToDrive,
  onEmailReports,
  canCustomizeExportResults,
}: React.PropsWithChildren<UIProps>) {
  const reportSettings = React.useMemo(() => {
    return getReportSettings(activityType);
  }, [activityType]);
  const {
    state: { open: paywallOpen, touch },
    dispatch,
  } = usePaywallContext();

  return (
    <>
      <Dialog
        id="export-reports"
        size="extraLarge"
        padding="none"
        open={open}
        title={translate('Export Results')}
        closeLabel={translate('Close')}
        onClose={onClose}
      >
        {({ headingRef, title }) => (
          <ModalMenuProvider>
            <ModalMenu>
              <ModalMenuSidebar>
                <div className="pt-20 px-8 py-6 md:py-8">
                  <h2 className="flex m-0">
                    <DialogTitle weight="bold" ref={headingRef}>
                      {title}
                    </DialogTitle>
                  </h2>
                </div>
                <div className="pb-9">
                  <TabList label="Report export options" orientation="vertical">
                    {reportSettings[ReportTypes.WholeClassExcel] && (
                      <ModalMenuTab>
                        {translate('Complete Results Excel')}
                      </ModalMenuTab>
                    )}
                    {reportSettings[ReportTypes.IndividualStudentPDF] && (
                      <ModalMenuTab>
                        {translate('Individual Student PDFs')}
                      </ModalMenuTab>
                    )}
                    {reportSettings[ReportTypes.QuestionSpecificPDF] && (
                      <ModalMenuTab>
                        {translate('Results Summary PDF')}
                      </ModalMenuTab>
                    )}
                    {reportSettings[ReportTypes.EmptyQuiz] && (
                      <ModalMenuTab>{translate('Answer Key PDF')}</ModalMenuTab>
                    )}
                  </TabList>
                </div>
              </ModalMenuSidebar>
              <ModalMenuContent>
                <TabPanels>
                  {reportSettings[ReportTypes.WholeClassExcel] && (
                    <TabPanel>
                      <ExportReportsCompleteResults
                        canCustomizeExportResults={canCustomizeExportResults}
                        downloadReports={onDownloadReports}
                        emailReportsToTeacher={onEmailReports}
                        uploadToDrive={onUploadToDrive}
                        activityInstanceId={activityInstanceId}
                        initialOptions={
                          reportSettings[ReportTypes.WholeClassExcel]
                        }
                        isSaveToDriveDisabled={isSaveToDriveDisabled}
                      />
                    </TabPanel>
                  )}
                  {reportSettings[ReportTypes.IndividualStudentPDF] && (
                    <TabPanel>
                      <ExportReportsIndividual
                        canCustomizeExportResults={canCustomizeExportResults}
                        downloadReports={onDownloadReports}
                        emailReportsToTeacher={onEmailReports}
                        uploadToDrive={onUploadToDrive}
                        activityInstanceId={activityInstanceId}
                        initialOptions={
                          reportSettings[ReportTypes.IndividualStudentPDF]
                        }
                        isSaveToDriveDisabled={isSaveToDriveDisabled}
                      />
                    </TabPanel>
                  )}
                  {reportSettings[ReportTypes.QuestionSpecificPDF] && (
                    <TabPanel>
                      <ExportReportsSummary
                        canCustomizeExportResults={canCustomizeExportResults}
                        downloadReports={onDownloadReports}
                        emailReportsToTeacher={onEmailReports}
                        uploadToDrive={onUploadToDrive}
                        activityInstanceId={activityInstanceId}
                        initialOptions={
                          reportSettings[ReportTypes.QuestionSpecificPDF]
                        }
                        isSaveToDriveDisabled={isSaveToDriveDisabled}
                      />
                    </TabPanel>
                  )}
                  {reportSettings[ReportTypes.EmptyQuiz] && (
                    <TabPanel>
                      <ExportReportsAnswerKey
                        canCustomizeExportResults={canCustomizeExportResults}
                        downloadReports={onDownloadReports}
                        emailReportsToTeacher={onEmailReports}
                        uploadToDrive={onUploadToDrive}
                        activityInstanceId={activityInstanceId}
                        initialOptions={reportSettings[ReportTypes.EmptyQuiz]}
                        isSaveToDriveDisabled={isSaveToDriveDisabled}
                      />
                    </TabPanel>
                  )}
                </TabPanels>
              </ModalMenuContent>
            </ModalMenu>
          </ModalMenuProvider>
        )}
      </Dialog>

      <ExportReportsPaywall
        open={paywallOpen}
        onClose={() => dispatch({ type: 'CLOSE' })}
        paywallTouch={touch}
      />
    </>
  );
}
